import React, { useMemo, useRef, useState } from 'react';

import { Box, TextField, Typography } from '@mui/material';
import { ZodError } from 'zod';

import type { StandardSchemaV1Issue } from '@tanstack/react-form';

export interface NameInputProps {
  value: string | undefined;
  onChange: (value: string) => void;
  error?: StandardSchemaV1Issue[] | ZodError<any> | string;
  onFocus?: (event?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (event?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  maxLength?: number;
  isEmpty?: boolean;
  size?: number;
}

export const NameInput = ({
  value,
  onChange,
  error,
  onFocus,
  onBlur,
  maxLength,
  isEmpty: isEmptyOverride,
  size = 14
}: NameInputProps) => {
  const [shake, setShake] = useState(false);

  const isEmpty = isEmptyOverride ?? (!value || value.length === 0);
  const messages = useMemo<string[]>(() => {
    if (!error) return [];
    if (typeof error === 'string') return [error];
    if (error instanceof ZodError) {
      return error.issues.map(i => i.message);
    }
    // Fallback: assume TanStack StandardSchemaV1Issue[]
    try {
      return error.map(e => e.message);
    } catch {
      return [];
    }
  }, [error]);
  const hasErrors = messages.length > 0;
  const sxShake = useMemo(
    () =>
      shake
        ? {
            animation: 'rb-shake 0.4s ease',
            '@keyframes rb-shake': {
              '10%, 90%': { transform: 'translate3d(-1px, 0, 0)' },
              '20%, 80%': { transform: 'translate3d(2px, 0, 0)' },
              '30%, 50%, 70%': { transform: 'translate3d(-4px, 0, 0)' },
              '40%, 60%': { transform: 'translate3d(4px, 0, 0)' }
            }
          }
        : {},
    [shake]
  );

  const inputRef = useRef<HTMLInputElement | null>(null);

  // When confirming interactions happen and we are in error state, shake
  const ANIMATION_DURATION_MS = 400; // 0.4s CSS animation duration
  const SHAKE_DURATION_MS = ANIMATION_DURATION_MS + 50; // Allow a bit more than the CSS animation to ensure it completes
  const triggerShakeIfError = () => {
    if (hasErrors) {
      setShake(true);
      setTimeout(() => {
        setShake(false);
      }, SHAKE_DURATION_MS);
    }
  };

  return (
    <Box
      className='name-input'
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
        minWidth: 0,
        position: 'relative'
      }}
    >
      <TextField
        error={hasErrors}
        inputRef={inputRef}
        slotProps={
          maxLength
            ? {
                htmlInput: {
                  maxLength
                }
              }
            : undefined
        }
        sx={{
          flex: 1,
          width: 'auto',
          maxWidth: '100%',
          '.MuiInputBase-formControl': {
            width: '100%'
          },
          '.MuiInputBase-root': {
            input: {
              height: '100%',
              padding: '4px 6px',
              fontSize: theme => theme.typography.pxToRem(size)
            },
            backgroundColor: 'info.light',
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent'
            },

            '&.Mui-focused': {
              '.MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main'
              }
            },
            'input:focus': {
              backgroundColor: 'info.dark'
            },
            ...sxShake,
            ...(isEmpty
              ? {
                  backgroundColor: 'error.light',
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: 'error.dark'
                  }
                }
              : {})
          }
        }}
        value={value ?? ''}
        onBlur={event => {
          triggerShakeIfError();
          onBlur?.(event);
        }}
        onChange={event => {
          const raw = event.target.value;
          const nextVal = typeof maxLength === 'number' ? raw.slice(0, maxLength) : raw;
          onChange(nextVal);
        }}
        onFocus={event => {
          onFocus?.(event);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            triggerShakeIfError();
          }
        }}
      />

      {hasErrors && (
        <Typography
          className='error-message'
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            fontSize: 12,
            color: 'error.main',
            mt: 0.5,
            whiteSpace: 'nowrap'
          }}
        >
          {messages.join(', ')}
        </Typography>
      )}
    </Box>
  );
};
